
import { motion } from "framer-motion";
import DashboardStat from "@/components/dashboard/DashboardStat";
import { fadeInUp } from "@/utils/animations";
import { Form, Lead } from "@/types";
import { Skeleton } from "@/components/ui/skeleton";

interface DashboardStatisticsProps {
  isLoading: boolean;
  forms: Form[];
  recentLeads: Lead[];
}

const DashboardStatistics = ({ isLoading, forms, recentLeads }: DashboardStatisticsProps) => {
  // Calculate stats
  const totalLeadCount = recentLeads.length;
  
  // Calculate total views across all forms
  const totalViews = forms.reduce((sum, form) => sum + (form.views || 0), 0);
  
  // Calculate conversion rate 
  const conversionRate = totalViews > 0 
    ? Math.round((totalLeadCount / totalViews) * 100) 
    : 0;

  // Generate estimated clicks (would normally come from backend)
  // This is a placeholder until we implement click storage in database
  const estimatedClicks = Math.floor(totalViews * 0.45); // Estimate 45% of views result in clicks
  
  // Click-to-lead conversion rate
  const clickConversionRate = estimatedClicks > 0
    ? Math.round((totalLeadCount / estimatedClicks) * 100)
    : 0;

  // For new users, we'll use the empty state with skeletal numbers
  const isNewUser = !isLoading && totalViews === 0 && totalLeadCount === 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 w-full">
      {isLoading ? (
        // Skeleton loaders for loading state
        <>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-32 rounded-lg bg-muted/50 animate-pulse"
            ></div>
          ))}
        </>
      ) : isNewUser ? (
        // Skeleton state for new users with text labels
        <>
          <motion.div key={1} {...fadeInUp} transition={{ delay: 0.1 }}>
            <DashboardStat
              title="Total Views"
              value={<Skeleton className="h-8 w-16" />}
              subtitle="Form impressions"
              tooltip="Total number of unique visitors who have viewed your forms"
            />
          </motion.div>
          
          <motion.div key={2} {...fadeInUp} transition={{ delay: 0.2 }}>
            <DashboardStat
              title="Estimated Clicks"
              value={<Skeleton className="h-8 w-16" />}
              subtitle="User interactions"
              tooltip="Estimated number of clicks on form elements (buttons, options, etc.)"
            />
          </motion.div>
          
          <motion.div key={3} {...fadeInUp} transition={{ delay: 0.3 }}>
            <DashboardStat
              title="Total Leads"
              value={<Skeleton className="h-8 w-16" />}
              subtitle="Across all forms"
              tooltip="Total number of submissions received from all your forms"
            />
          </motion.div>
          
          <motion.div key={4} {...fadeInUp} transition={{ delay: 0.4 }}>
            <DashboardStat
              title="Conversion Rate"
              value={<Skeleton className="h-8 w-16" />}
              subtitle="Views to leads"
              tooltip="Percentage of form views that resulted in submissions"
            />
          </motion.div>
        </>
      ) : (
        // Actual stats
        <>
          <motion.div {...fadeInUp} transition={{ delay: 0.1 }}>
            <DashboardStat
              title="Total Views"
              value={totalViews.toString()}
              subtitle="Form impressions"
              tooltip="Total number of unique visitors who have viewed your forms"
            />
          </motion.div>
          
          <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
            <DashboardStat
              title="Estimated Clicks"
              value={estimatedClicks.toString()}
              subtitle="User interactions"
              tooltip="Estimated number of clicks on form elements (buttons, options, etc.)"
            />
          </motion.div>
          
          <motion.div {...fadeInUp} transition={{ delay: 0.3 }}>
            <DashboardStat
              title="Total Leads"
              value={totalLeadCount.toString()}
              subtitle="Across all forms"
              tooltip="Total number of submissions received from all your forms"
            />
          </motion.div>
          
          <motion.div {...fadeInUp} transition={{ delay: 0.4 }}>
            <DashboardStat
              title="Conversion Rate"
              value={`${conversionRate}%`}
              subtitle="Views to leads"
              tooltip="Percentage of form views that resulted in submissions"
            />
          </motion.div>
        </>
      )}
    </div>
  );
};

export default DashboardStatistics;
